import { action, observable } from 'mobx'
import { catchError, EMPTY, switchMap } from 'rxjs'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import type { CursorPageQuery, CursorPaged } from '@/infra/paging'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { withAbortSignal } from '@/utils/rx-util'

/**
 * A page query with the current search text.
 */
export interface SearchableSelectQuery extends Partial<CursorPageQuery> {
  searchText?: string
}

/**
 * Searches a page of the items to select from.
 */
export type SearchSelectableItemsFn<T> = (
  query: SearchableSelectQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<T>>

/**
 * Paging, search and the selection for a single-item picker.
 */
export class SearchableSelectViewModel<T> {
  /**
   * The text the items are searched by.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * The selected item.
   */
  @observable accessor selected: T | null = null

  /**
   * Pages through the matching items.
   */
  readonly scrollProvider: InfiniteScrollProvider<T>

  /**
   * Refetches the first page whenever the search text changes.
   */
  readonly query$

  constructor(private readonly _searchItems: SearchSelectableItemsFn<T>) {
    this.scrollProvider = new InfiniteScrollProvider<T>({
      limit: 20,
      fetchItems: ({ paging, signal }) =>
        this._searchItems(
          {
            limit: paging.limit,
            after: paging.after,
            searchText: this.searchText.value || undefined,
          },
          signal,
        ),
    })
    this.query$ = this.searchText.debouncedValue$.pipe(
      switchMap(() =>
        withAbortSignal((signal) => this.scrollProvider.fetch({ signal })).pipe(
          // The scroll provider holds the error, and the next search tries again.
          catchError(() => EMPTY),
        ),
      ),
    )
  }

  /**
   * Updates the search text. The debounced stream reloads the list.
   */
  @action.bound
  triggerSearch(text: string) {
    this.searchText.change(text)
  }

  /**
   * Selects an item. Passing nothing clears the choice.
   */
  @action.bound
  select(item: T | null | undefined) {
    this.selected = item ?? null
  }

  /**
   * Clears the choice and the search text.
   */
  @action.bound
  reset() {
    this.select(null)
    this.searchText.clear()
  }
}
